import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Card, CardBody, Badge, Button } from "reactstrap";
import { toast } from "sonner";
import { useGetProjectByIdQuery, useGetClientQuery, useUpdateProjectMutation, useGetAllWorkLogsQuery } from "store/features/client/clientManagementApi";
import { useGetAllDesignersQuery, useGetAllDevelopersQuery, useGetAllProjectManagersQuery } from "store/features/developer/developerManagementApi";
import { useGetAllUsersQuery } from "store/features/users/userManagementApi";
import EditProjectModal from "components/Modals/EditProjectModal";
import ProjectSummaryModal from "components/Modals/ProjectSummaryModal";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";

const ProjectDetails = () => {
  const user = useSelector(selectUser);

  const { id } = useParams();
  const { data: project, isLoading: projectLoading, refetch } = useGetProjectByIdQuery(id, {
    skip: !id,
  });
  const { data: allWorkLogs = [], isLoading: workLogsLoading } = useGetAllWorkLogsQuery();

  const { data: client, isLoading: clientLoading } = useGetClientQuery(project?.client, {
    skip: !project?.client
  });

  console.log("client", client);
  const { data: developers = [] } = useGetAllDevelopersQuery();
  const { data: designers = [] } = useGetAllDesignersQuery();
  const { data: projectManagers = [] } = useGetAllProjectManagersQuery();
  const { data: allUsers = [] } = useGetAllUsersQuery();

  const [updateProject, { isLoading: isUpdatingProgress }] = useUpdateProjectMutation();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  const getAllAssignedMembers = useMemo(() => {
    if (!project) return [];
    const memberMap = new Map();

    const addMembers = (list, roleLabel) => {
      if (!list) return;
      const items = Array.isArray(list) ? list : [list];
      items.forEach(item => {
        const memberId = typeof item === 'object' && item !== null ? item.id : item;
        const userObj =
          developers?.find(d => String(d.id) === String(memberId)) ||
          projectManagers?.find(d => String(d.id) === String(memberId)) ||
          designers?.find(d => String(d.id) === String(memberId)) ||
          allUsers?.find(u => String(u.id) === String(memberId)) ||
          (typeof item === 'object' && item !== null ? item : null);

        if (memberId && !memberMap.has(String(memberId))) {
          memberMap.set(String(memberId), {
            id: String(memberId),
            name: userObj?.nickname || userObj?.first_name || userObj?.username || userObj?.name || userObj?.user_info?.name || `Member #${memberId}`,
            role: userObj?.role || roleLabel
          });
        }
      });
    };

    addMembers(project.assigned_project_manager, 'Project Manager');
    addMembers(project.assigned_frontend_dev, 'Frontend Developer');
    addMembers(project.assigned_backend_dev, 'Backend Developer');
    addMembers(project.assigned_designer, 'Designer');
    addMembers(project.assigned_marketing_officer, 'Marketing Officer');
    if (project.marketing_officer_id) {
      addMembers(project.marketing_officer_id, 'Marketing Officer');
    }
    addMembers(project.assigned_team, 'Team Member');

    return Array.from(memberMap.values());
  }, [project, developers, projectManagers, designers, allUsers]);

  const getMarketingOfficerName = useMemo(() => {
    if (!project) return "Not Assigned";

    const moId = typeof project.assigned_marketing_officer === 'object' && project.assigned_marketing_officer !== null
      ? project.assigned_marketing_officer.id
      : (project.assigned_marketing_officer || project.marketing_officer_id);

    if (moId) {
      const u = allUsers.find(user => String(user.id) === String(moId));
      if (u) return u.nickname || u.first_name || u.username || u.name || `User #${u.id}`;
    }

    if (Array.isArray(project.assigned_team) && project.assigned_team.length > 0) {
      const teamId = typeof project.assigned_team[0] === 'object' && project.assigned_team[0] !== null
        ? project.assigned_team[0].id
        : project.assigned_team[0];
      const u = allUsers.find(user => String(user.id) === String(teamId));
      if (u) return u.nickname || u.first_name || u.username || u.name || `User #${u.id}`;
    }

    const foundMember = getAllAssignedMembers.find(m => 
      m.role === 'Marketing Officer' || 
      m.role?.toLowerCase().includes('marketing') ||
      m.role?.toLowerCase().includes('sales')
    );
    if (foundMember) return foundMember.name;

    return "Not Assigned";
  }, [project, allUsers, getAllAssignedMembers]);

  const devProgressData = project?.developer_progress || {};
  const currentDevProgressVal = user?.id ? (devProgressData[user.id] ?? devProgressData[String(user.id)] ?? 0) : 0;
  const [myProgressVal, setMyProgressVal] = useState(currentDevProgressVal);

  useEffect(() => {
    if (user?.id && project?.developer_progress) {
      const val = project.developer_progress[user.id] ?? project.developer_progress[String(user.id)] ?? 0;
      setMyProgressVal(val);
    }
  }, [project?.developer_progress, user?.id]);

  const isCurrentUserAssigned = useMemo(() => {
    if (!user?.id) return false;
    return getAllAssignedMembers.some(m => String(m.id) === String(user.id));
  }, [getAllAssignedMembers, user?.id]);

  const handleSaveMyProgress = async () => {
    if (!user?.id || !project?.id) return;
    try {
      const valNum = Math.min(100, Math.max(0, Number(myProgressVal) || 0));
      const oldVal = Number(
        project.developer_progress?.[user.id] ??
        project.developer_progress?.[String(user.id)] ??
        0
      );
      const delta = valNum - oldVal;

      const updatedDevProgress = {
        ...(project.developer_progress || {}),
        [String(user.id)]: valNum,
        [user.id]: valNum
      };

      const currentMonth = new Date().toISOString().slice(0, 7);
      let history = [];
      if (project.developer_progress_history) {
        history = Array.isArray(project.developer_progress_history)
          ? [...project.developer_progress_history]
          : (typeof project.developer_progress_history === 'string'
              ? JSON.parse(project.developer_progress_history)
              : []);
      }

      if (delta !== 0) {
        history.push({
          user_id: String(user.id),
          month: currentMonth,
          old_progress: oldVal,
          new_progress: valNum,
          delta: delta,
          updated_at: new Date().toISOString()
        });
      }

      const assignedCount = getAllAssignedMembers.length || 1;
      let totalProgressSum = 0;

      if (getAllAssignedMembers.length > 0) {
        getAllAssignedMembers.forEach(m => {
          const mVal = updatedDevProgress[m.id] ?? updatedDevProgress[String(m.id)] ?? 0;
          totalProgressSum += Number(mVal);
        });
      } else {
        totalProgressSum = valNum;
      }

      const newOverallProgress = Math.min(100, Math.round(totalProgressSum / assignedCount));

      await updateProject({
        id: project.id,
        developer_progress: updatedDevProgress,
        developer_progress_history: history,
        progress: newOverallProgress
      }).unwrap();

      toast.success("Your progress updated successfully!");
      refetch();
    } catch (err) {
      console.error("Progress update error:", err);
      toast.error("Failed to update progress");
    }
  };

  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': 'warning',
      'In Progress': 'primary',
      'Completed': 'success',
      'On Hold': 'danger'
    };
    return statusConfig[status] || 'secondary';
  };

  const isUserAssigned = (assignedArray, userId) => {
    if (!assignedArray || !Array.isArray(assignedArray)) return false;
    return assignedArray.some(idOrUser => {
      if (typeof idOrUser === "object" && idOrUser !== null) {
        return String(idOrUser.id) === String(userId);
      }
      return String(idOrUser) === String(userId);
    });
  };

  const getAssignedDevelopers = () => {
    if (!project?.assigned_project_manager || !projectManagers?.length) return [];
    return projectManagers.filter(dev => isUserAssigned(project.assigned_project_manager, dev.id));
  };

  const getAssignedFrontendDevelopers = () => {
    if (!project?.assigned_frontend_dev || !developers?.length) return [];
    return developers.filter(dev => isUserAssigned(project.assigned_frontend_dev, dev.id));
  };

  const getAssignedBackendDevelopers = () => {
    if (!project?.assigned_backend_dev || !developers?.length) return [];
    return developers.filter(dev => isUserAssigned(project.assigned_backend_dev, dev.id));
  };

  const getAssignedDesigners = () => {
    if (!project?.assigned_designer || !designers?.length) return [];
    return designers.filter(dev => isUserAssigned(project.assigned_designer, dev.id));
  };

  const handleProjectUpdate = () => {
    refetch(); // Refetch project data after update
    toggleEditModal();
  };

  const getFileViewLink = (fileUrl) => {
    if (!fileUrl) return null;
    return `${fileUrl}`;
  };

  const getPriorityBadge = priority => {
    const priorityConfig = {
      1: { color: "danger", text: "High" },
      2: { color: "warning", text: "Medium" },
      3: { color: "success", text: "Low" },
      "": { color: "secondary", text: "Not Set" },
    };
    const config = priorityConfig[priority] || priorityConfig[""];
    return `${config.text}`;
  };

  // Filter work logs by project ID
  const projectWorkLogs = useMemo(() => {
    if (!allWorkLogs || !project) return [];
    return allWorkLogs.filter(log =>
      log.project_id === project.id ||
      log.project_id === parseInt(project.id) ||
      log.project_id?.toString() === project.id?.toString()
    );
  }, [allWorkLogs, project]);

  if (projectLoading || clientLoading || workLogsLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb
          title="Project Details"
          breadcrumbItem={
            <>
              <Link to="/clients">Clients</Link> / Project Details
            </>
          }
        />

        <Row>
          {/* Project Information */}

          <div className="mb-3 d-flex justify-content-end gap-2">
            {(user?.role == "super_admin" || user?.role == "admin") && (
              <Button color="secondary" onClick={() => setSummaryModalOpen(true)}>
                <i className="fas fa-print me-1"></i> Print Summary PDF
              </Button>
            )}
            {(user?.role == "super_admin" || user?.role == "admin" || user?.role == "marketing") ? (
              <Button color="primary" onClick={toggleEditModal}>
                <i className="fas fa-edit me-1"></i> Edit Project
              </Button>
            ) : ""}
          </div>

          <Col lg={8}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0 text-primary" style={{ fontSize: "18px" }}>{project?.name}</h4>
                  <Badge color={getStatusBadge(project?.status)} className="fs-6">
                    {project?.status}
                  </Badge>
                </div>

                <Row>
                  {(user?.role == "super_admin" || user?.role == "admin") ? (
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Description</label>
                        <p className="text-muted mb-0">{project?.description || "No description provided"}</p>
                      </div>
                    </Col>)

                    :
                    (<Col lg={12}>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Description</label>
                        <p className="text-muted mb-0">{project?.description || "No description provided"}</p>
                      </div>
                    </Col>

                    )}
                  {(user?.role == "super_admin" || user?.role == "admin") && (
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Budget</label>
                        <p className="text-muted mb-0">{project?.budget || "0.00"}</p>
                      </div>
                    </Col>

                  )}
                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Start Date</label>
                      <p className="text-muted mb-0">{project?.start_date || "Not set"}</p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">End Date</label>
                      <p className="text-muted mb-0">{project?.end_date || "Not set"}</p>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Progress</label>
                      <div className="progress mb-2">
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${project?.progress || 0}%` }}
                          aria-valuenow={project?.progress || 0}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {project?.progress || 0}%
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold text-primary">Marketing Officer</label>
                      <p className="text-dark fw-bold mb-0 fs-6">
                        <i className="fas fa-user-check me-2 text-success"></i>
                        {getMarketingOfficerName}
                      </p>
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Priority</label>
                      <p className="text-muted mb-0">{getPriorityBadge(project?.priority_status)}</p>
                    </div>
                  </Col>

                  {/* Payment Schedules & Invoice Dates */}
                  <Col lg={12} className="mt-3">
                    <div className="p-3 border rounded bg-light mb-3">
                      <h6 className="fw-bold text-primary mb-3">
                        <i className="fas fa-file-invoice-dollar me-2"></i>Planned Payment Schedule & Invoice Send Dates
                      </h6>
                      <Row>
                        <Col md={6} className="mb-2">
                          <div className="p-2 border rounded bg-white">
                            <span className="fw-bold text-primary d-block mb-1">1st Payment Schedule</span>
                            <div className="small"><strong>Due Date:</strong> {project?.first_payment_date || "Not set"}</div>
                            <div className="small"><strong>Amount:</strong> ৳ {parseFloat(project?.first_payment_amount || 0).toLocaleString()}</div>
                            <div className="small"><strong>Invoice Send Date:</strong> <Badge color="dark" className="ms-1">{project?.first_payment_invoice_date || "Not set"}</Badge></div>
                            <div className="small mt-1"><strong>Status:</strong> <Badge color={project?.first_payment_status === "Paid" ? "success" : project?.first_payment_status === "Invoice Sent" ? "info" : "warning"} className="ms-1">{project?.first_payment_status || "Pending"}</Badge></div>
                          </div>
                        </Col>
                        <Col md={6} className="mb-2">
                          <div className="p-2 border rounded bg-white">
                            <span className="fw-bold text-primary d-block mb-1">2nd Payment Schedule</span>
                            <div className="small"><strong>Due Date:</strong> {project?.second_payment_date || "Not set"}</div>
                            <div className="small"><strong>Amount:</strong> ৳ {parseFloat(project?.second_payment_amount || 0).toLocaleString()}</div>
                            <div className="small"><strong>Invoice Send Date:</strong> <Badge color="dark" className="ms-1">{project?.second_payment_invoice_date || "Not set"}</Badge></div>
                            <div className="small mt-1"><strong>Status:</strong> <Badge color={project?.second_payment_status === "Paid" ? "success" : project?.second_payment_status === "Invoice Sent" ? "info" : "warning"} className="ms-1">{project?.second_payment_status || "Pending"}</Badge></div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Google Calendar & Scheduled Meeting */}
                  {(project?.google_calendar_meeting_link || project?.google_calendar_meeting_date) && (
                    <Col lg={12}>
                      <div className="p-3 border rounded border-success bg-light mb-3">
                        <h6 className="fw-bold text-success mb-2">
                          <i className="fab fa-google me-2"></i>Google Calendar Scheduled Meeting
                        </h6>
                        <Row className="small">
                          <Col md={6}>
                            <strong>Scheduled Date & Time:</strong> {project.google_calendar_meeting_date ? new Date(project.google_calendar_meeting_date).toLocaleString() : "Not Scheduled"}
                          </Col>
                          <Col md={6}>
                            <strong>Meeting Link:</strong>{" "}
                            {project.google_calendar_meeting_link ? (
                              <a href={project.google_calendar_meeting_link} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success ms-2">
                                <i className="fas fa-video me-1"></i> Join Google Meeting
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  )}

                  {/* Interactive Developer Progress Update Card */}
                  {isCurrentUserAssigned && (() => {
                    const devTeamMembers = getAllAssignedMembers.filter(m => 
                      m.role !== 'Marketing Officer' && 
                      !m.role?.toLowerCase().includes('marketing')
                    );
                    const devCount = devTeamMembers.length || 1;
                    const allocatedShareStr = (100 / devCount).toFixed(1);

                    return (
                      <Col lg={12} className="mt-2">
                        <div className="p-3 border rounded border-primary bg-light mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 text-primary fw-bold">
                              <i className="fas fa-sliders-h me-2"></i>Update Your Work Progress
                            </h6>
                            <Badge color="info">
                              Allocated Work Share: {allocatedShareStr}%
                            </Badge>
                          </div>
                          <p className="text-muted small mb-3">
                            You are 1 of {devCount} assigned technical team members. Update your completion status below (0% - 100%). It will automatically calculate towards overall project progress.
                          </p>
                          <div className="d-flex align-items-center gap-3">
                          <input
                            type="range"
                            className="form-range flex-grow-1"
                            min="0"
                            max="100"
                            value={myProgressVal}
                            onChange={(e) => setMyProgressVal(e.target.value)}
                          />
                          <div className="d-flex align-items-center gap-1" style={{ minWidth: "110px" }}>
                            <input
                              type="number"
                              className="form-control form-control-sm text-center fw-bold"
                              min="0"
                              max="100"
                              value={myProgressVal}
                              onChange={(e) => setMyProgressVal(e.target.value)}
                            />
                            <span className="fw-bold">%</span>
                          </div>
                          <Button
                            color="success"
                            size="sm"
                            onClick={handleSaveMyProgress}
                            disabled={isUpdatingProgress}
                          >
                            {isUpdatingProgress ? "Saving..." : "Save Progress"}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  );
                })()}

                  {/* Team Members Progress Breakdown */}
                  {(() => {
                    const techMembers = getAllAssignedMembers.filter(m => 
                      m.role !== 'Marketing Officer' && 
                      !m.role?.toLowerCase().includes('marketing')
                    );
                    if (techMembers.length === 0) return null;

                    const techCount = techMembers.length;
                    const eachShareStr = (100 / techCount).toFixed(1);

                    return (
                      <Col lg={12} className="mt-1">
                        <div className="p-3 border rounded bg-white mb-3">
                          <h6 className="card-title text-dark mb-3" style={{ fontSize: "14px" }}>
                            Team Progress Breakdown (Share: {eachShareStr}% each)
                          </h6>
                          <Row>
                            {techMembers.map((member) => {
                              const memberDevVal = devProgressData[member.id] ?? devProgressData[String(member.id)] ?? 0;
                              const memberContribution = (memberDevVal * (1 / techCount)).toFixed(1);
                              return (
                                <Col lg={6} key={member.id} className="mb-2">
                                  <div className="p-2 border rounded bg-light">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="fw-semibold fs-13">{member.name}</span>
                                      <Badge color="primary">{memberDevVal}% Completed</Badge>
                                    </div>
                                    <div className="progress" style={{ height: "6px" }}>
                                      <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{ width: `${memberDevVal}%` }}
                                        aria-valuenow={memberDevVal}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                      ></div>
                                    </div>
                                    <small className="text-muted fs-11 mt-1 d-block">
                                      Contribution: {memberContribution}% of total project
                                    </small>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      </Col>
                    );
                  })()}

                  {/* <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Transactions</label>

                      {
                        project?.payment ?
                          project?.payment.map((payment, index) => (
                            <div key={payment.id}>
                              <small className="fs-6">
                                Payment {index + 1}:{" "}
                                <span className="fw-semibold">
                                  {payment.amount || "0.00"}
                                </span>
                              </small>
                            </div>
                          ))

                          : <p>No payments</p>


                      }
                    </div>
                  </Col> */}

                  {(user?.role == "super_admin" || user?.role == "admin") && (
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Transactions</label>

                        {project?.payment && project.payment.length > 0 ? (
                          <div className="">
                            {project.payment.map((payment, index) => (
                              <div
                                key={payment.id}
                                className="d-flex justify-content-between mb-1"
                              >
                                <div className="">
                                  <small className="fs-6">Payment {index + 1}:</small>
                                  <br />
                                  <small className="">{payment?.date}</small>
                                </div>
                                <small className="fw-semibold fs-6">
                                  {/* {parseFloat(payment.amount || 0).toFixed(0).toLocaleString()} */}
                                  {Number(parseFloat(payment.amount).toFixed(0)).toLocaleString()}
                                </small>
                              </div>
                            ))}

                            <hr className="my-2" />

                            {(() => {
                              const budget = parseFloat(project?.budget || 0);
                              const totalPaid = project.payment.reduce(
                                (sum, p) => sum + parseFloat(p.amount || 0),
                                0
                              );
                              const remaining = budget - totalPaid;

                              return (
                                <>
                                  <div className="d-flex justify-content-between align-items-center fs-5">
                                    <small className="fw-semibold text-success">Total Paid</small>
                                    <small className="fw-semibold text-success">

                                      {Number(totalPaid.toFixed(0)).toLocaleString()}
                                    </small>
                                  </div>

                                  <div className="d-flex justify-content-between align-items-center fs-5">
                                    <small
                                      className={`fw-semibold ${remaining <= 0 ? "text-success" : "text-danger"
                                        }`}
                                    >
                                      Remaining
                                    </small>
                                    <small
                                      className={`fw-semibold ${remaining <= 0 ? "text-success" : "text-danger"
                                        }`}
                                    >
                                      {Number(remaining.toFixed(0)).toLocaleString()}
                                    </small>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <p className="text-muted mb-0">No payments</p>
                        )}
                      </div>
                    </Col>

                  )}




                </Row>

                {/* File Attachments Section */}

                {(user?.role == "super_admin" || user?.role == "admin") && (

                  <div className="mt-4">
                    <h5 className="card-title mb-3 text-primary" style={{ fontSize: "18px" }}>Project Documents</h5>
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Project Proposal</label>
                          <div>
                            {project?.project_proposal ? (
                              <a
                                href={getFileViewLink(project.project_proposal)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                              >
                                <i className="fas fa-file-pdf me-1"></i> View Proposal
                              </a>
                            ) : (
                              <span className="text-muted">No proposal uploaded</span>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Contract</label>
                          <div>
                            {project?.contract ? (
                              <a
                                href={getFileViewLink(project.contract)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                              >
                                <i className="fas fa-file-contract me-1"></i> View Contract
                              </a>
                            ) : (
                              <span className="text-muted">No contract uploaded</span>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Invoice 1</label>
                          <div>
                            {project?.invoice_1 ? (
                              <a
                                href={getFileViewLink(project.invoice_1)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                              >
                                <i className="fas fa-file-invoice me-1"></i> View Invoice 1
                              </a>
                            ) : (
                              <span className="text-muted">No invoice uploaded</span>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Invoice 2</label>
                          <div>
                            {project?.invoice_2 ? (
                              <a
                                href={getFileViewLink(project.invoice_2)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                              >
                                <i className="fas fa-file-invoice me-1"></i> View Invoice 2
                              </a>
                            ) : (
                              <span className="text-muted">No invoice uploaded</span>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                )}



                {/* <div className="mt-4">
                  <h5 className="card-title mb-3 text-primary" style={{ fontSize: "18px" }}>Work Logs</h5>
                  <Row>
                    {project?.work_logs && project.work_logs.length > 0 && (
                      <Col lg={12}>
                        <div className="mb-3">
                          <div className="">
                            {project.work_logs.map((log, index) => (
                              <div key={log.id || index} className="border rounded p-3 mb-2 border-bottom" style={{ backgroundColor: "#F2F2F2" }}>
                                <div className="d-flex justify-content-between">
                                  <strong>{log.user_name} ({log.user_role})</strong>
                                  <small className="text-muted">{log.date}</small>
                                </div>
                                <div className="mt-1">{log.log}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div> */}

                <div className="mt-4">
                  <h5 className="card-title mb-3 text-primary" style={{ fontSize: "18px" }}>Work Logs</h5>
                  <Row>
                    {projectWorkLogs.length > 0 ? (
                      <Col lg={12}>
                        <div className="mb-3">
                          <div className="">
                            {projectWorkLogs.map((log, index) => (
                              <div key={log.id || index} className="border rounded p-3 mb-3" style={{ backgroundColor: "#F2F2F2" }}>
                                {/* Header Row - Name, Date, Hours, Status */}
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <strong className="fs-6">{log.employer_name} ({log.employer_role})</strong>
                                  </div>
                                  <div className="text-end d-flex gap-3 align-items-center">
                                    <div className="text-muted small">
                                      {new Date(log.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-info">{log.hours_spent}h</span>
                                      <span className={`badge ${log.work_status === 'Completed' ? 'bg-success' :
                                        log.work_status === 'In Progress' ? 'bg-primary' :
                                          log.work_status === 'Pending' ? 'bg-warning' :
                                            'bg-danger'
                                        }`}>
                                        {log.work_status}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Title (if exists) */}
                                {log.title && (
                                  <div className="mb-2">
                                    <strong className="text-dark">{log.title}</strong>
                                  </div>
                                )}

                                {/* Work Summary */}
                                <div className="mt-2">
                                  <div className="text-dark">{log.work_summary}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    ) : (
                      <Col lg={12}>
                        <div className="text-center py-4">
                          <i className="fas fa-clipboard-list fa-2x text-muted mb-2"></i>
                          <p className="text-muted mb-0">
                            {workLogsLoading ? "Loading work logs..." : "No work logs available for this project"}
                          </p>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>

              </CardBody>
            </Card>
          </Col>

          {/* Client Information & Team */}



          <Col lg={4}>
            {/* Client Card */}

            {(user?.role == "super_admin" || user?.role == "admin") && (
              <Card className="mb-4">
                <CardBody>
                  <h4 style={{ fontSize: "18px" }} className="card-title mb-3 text-primary">Client Information</h4>
                  {client ? (
                    <>
                      <div className="mb-2 d-flex gap-2">
                        <label className="form-label fw-semibold">Company Name:</label>
                        <p className="text-muted mb-0">{client.company_name}</p>
                      </div>
                      <div className="mb-2 d-flex gap-2">
                        <label className="form-label fw-semibold">Contact Person:</label>
                        <p className="text-muted mb-0">{client.contact_person || "N/A"}</p>
                      </div>
                      {/* <div className="mb-2 d-flex gap-2">
                      <label className="form-label fw-semibold">Email:</label>
                      <p className="text-muted mb-0">{client.user_info?.personal_email || "N/A"}</p>
                    </div> */}
                      <div className="mb-2 d-flex gap-2">
                        <label className="form-label fw-semibold">Phone:</label>
                        <p className="text-muted mb-0">{client.contact_number || "N/A"}</p>
                      </div>
                      <div className="mb-2 d-flex gap-2">
                        <label className="form-label fw-semibold">Address:</label>
                        <p className="text-muted mb-0">{client.address || "N/A"}</p>
                      </div>
                      <div className="mb-2 d-flex gap-2">
                        <label className="form-label fw-semibold">Notes:</label>
                        <p className="text-muted mb-0">{client.notes || "N/A"}</p>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted">No client information available</p>
                  )}
                </CardBody>
              </Card>

            )}
            {/* Assigned Team Card */}
            <Card>
              <CardBody>
                <h4 className="card-title mb-3 text-primary" style={{ fontSize: "18px" }}>Assigned Team</h4>
                <h5 className="card-title mb-3 text-dark">Project Manager</h5>
                {getAssignedDevelopers().length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {getAssignedDevelopers().map(dev => (
                      <div key={dev.id} className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <div>
                          <h6 className="mb-0 fs-14">{dev.nickname}</h6>
                          {/* <small className="text-muted">{dev.personal_email}</small> */}
                        </div>
                        <Badge color="light" className="text-dark">
                          {dev.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No team members assigned</p>
                )}
                {/* </CardBody>
              <CardBody> */}
                <h5 className="card-title mb-3 mt-3 text-dark">FrontEnd Developer</h5>
                {getAssignedFrontendDevelopers().length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {getAssignedFrontendDevelopers().map(dev => (
                      <div key={dev.id} className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <div>
                          <h6 className="mb-0 fs-14">{dev.nickname}</h6>
                          {/* <small className="text-muted">{dev.personal_email}</small> */}
                        </div>
                        <Badge color="light" className="text-dark">
                          {dev.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No team members assigned</p>
                )}
                {/* </CardBody>
              <CardBody> */}
                <h5 className="card-title mb-3  mt-3 text-dark">Backend Developer</h5>

                {getAssignedBackendDevelopers().length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {getAssignedBackendDevelopers().map(dev => (
                      <div key={dev.id} className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <div>
                          <h6 className="mb-0 fs-14">{dev.nickname}</h6>
                          {/* <small className="text-muted">{dev.personal_email}</small> */}
                        </div>
                        <Badge color="light" className="text-dark">
                          {dev.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No team members assigned</p>
                )}
                {/* </CardBody>
              <CardBody> */}
                <h5 className="card-title mb-3  mt-3 text-dark">Designer</h5>
                {getAssignedDesigners().length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {getAssignedDesigners().map(dev => (
                      <div key={dev.id} className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <div>
                          <h6 className="mb-0 fs-14">{dev.nickname}</h6>
                          {/* <small className="text-muted">{dev.personal_email}</small> */}
                        </div>
                        <Badge color="light" className="text-dark">
                          {dev.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No team members assigned</p>
                )}
              </CardBody>
            </Card>
          </Col>


        </Row>

        {/* Edit Project Modal */}
        {project && (
          <>
            <EditProjectModal
              isOpen={editModalOpen}
              toggle={toggleEditModal}
              project={project}
              client={client}
              onProjectUpdate={handleProjectUpdate}
            />
            <ProjectSummaryModal
              isOpen={summaryModalOpen}
              toggle={() => setSummaryModalOpen(!summaryModalOpen)}
              project={project}
              client={client}
              assignedMembers={getAllAssignedMembers}
              workLogs={projectWorkLogs}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;